import { faMessage } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useRef } from 'react';
import { ActionFunctionArgs, useFetcher } from 'react-router-dom';
import { client } from '~/client';
import { graphql } from '~/gql';

type Props = {
  latest?: string;
  unread: number;
  onSuccess?: () => void;
};

const MarkAllAsRead = graphql(/* GraphQL */ `
  mutation MarkAllAsRead($before: ID!) {
    markAllAsRead(before: $before)
  }
`);

export async function markAllAsRead({ request }: ActionFunctionArgs) {
  const formData = await request.formData();

  const before = formData.get('before') as string;

  if (!before) {
    throw new Response('Missing before parameter', { status: 400 });
  }

  const result = await client.mutation(MarkAllAsRead, { before }).toPromise();

  // Return the count - optimistic UI update is handled client-side via onSuccess callback
  return result.data?.markAllAsRead ?? 0;
}

export function MarkAllAsReadButton({ latest, unread, onSuccess }: Props) {
  const fetcher = useFetcher<number>();
  const allRead = unread === 0;
  const submitting = fetcher.state === 'submitting';

  // Call onSuccess when mutation completes successfully
  // This triggers optimistic UI updates in the parent without a page reload
  const prevStateRef = useRef(fetcher.state);
  useEffect(() => {
    // Detect transition from 'loading' (after submission) back to 'idle' (complete)
    if (
      prevStateRef.current === 'loading' &&
      fetcher.state === 'idle' &&
      fetcher.data !== undefined
    ) {
      onSuccess?.();
    }
    prevStateRef.current = fetcher.state;
  }, [fetcher.state, fetcher.data, onSuccess]);

  const handleClick = () => {
    fetcher.submit(
      { before: latest ?? '' },
      { method: 'post', action: '/activity/mark-all-as-read' }
    );
  };

  return (
    <button
      className='ml-auto flex items-center gap-2 rounded border border-grey-20 p-1 text-xs text-secondary lg:p-2 lg:text-base'
      onClick={handleClick}
      disabled={allRead || submitting}
    >
      <FontAwesomeIcon className='hidden lg:block' icon={faMessage} />
      {allRead ? 'All read!' : submitting ? 'Loading...' : 'Mark all as read'}
    </button>
  );
}
